import { useMutation } from "@apollo/client";
import { SAVE_COMPANY_DOCUMENT_FOLDER } from "../api/save-company-document-folder";
import { GET_COMPANY_DOCUMENT_TREE } from "@/modules/documents/widgets/company-document-tree/api/get-company-document-tree";
import { GET_COMPANY_DOCUMENT_FOLDER_TREE } from "@/modules/documents/entities/company-document-folder-select/api/get-company-document-folder-tree";
import { useMoveCompanyDocumentNode } from "@/modules/documents/features/move-company-document-node";
import { useStructureIdSplit } from "./use-structure-id-split";
import { FolderFieldsType } from "./types";

export function useSaveCompanyDocumentFolder({
  afterSubmit,
  initialParentId,
}: {
  afterSubmit?: () => void;
  /** Родитель, с которым папка открыта в форме: его смена — это перенос. */
  initialParentId?: string | null;
} = {}) {
  const { split, loading: splitLoading } = useStructureIdSplit();
  const { moveFolderTo, loading: moveLoading } = useMoveCompanyDocumentNode();
  const [saveFolder, { loading }] = useMutation(SAVE_COMPANY_DOCUMENT_FOLDER, {
    refetchQueries: [
      GET_COMPANY_DOCUMENT_TREE,
      GET_COMPANY_DOCUMENT_FOLDER_TREE,
    ],
  });

  const save = async (fields: FolderFieldsType) => {
    const { organizationIds, positionIds } = split(fields.structureIds);
    // Очищенный выбор родителя приходит как `undefined`, а корень дерева — это `null`.
    const parentId = fields.parentId ?? null;

    // Перенос — отдельной мутацией, как при перетаскивании: она проверяет циклы
    // и права на папку-приёмник, которых сохранение папки может не делать.
    if (fields.id && parentId !== (initialParentId ?? null)) {
      await moveFolderTo(fields.id, parentId);
    }

    await saveFolder({
      variables: {
        folder: {
          id: fields.id,
          parentId,
          name: fields.name,
          color: fields.color,
          access: {
            organizationIds,
            positionIds,
            jobIds: fields.jobIds ?? [],
          },
        },
      },
    });

    afterSubmit?.();
  };

  return { save, loading: loading || splitLoading || moveLoading };
}
